/**
 * Career / Open Positions Configuration
 *
 * Single source of truth for job postings shown on /career.
 * The posting body itself is bilingual data in `CareerDetailPage.ts`
 * (`postingDetails`), rendered natively on the site — no backend required.
 *
 * `id` MUST be lowercase. It becomes the URL segment
 * (/company/career/{id}), and Netlify normalises request paths to lowercase:
 * an uppercase id makes the sitemap entry and the page's own canonical point
 * at a URL that 301s, which Ahrefs flags as '3XX redirect in sitemap' and
 * 'canonical points to redirect'.
 *
 * Lifecycle:
 *   - status 'urgent' | 'open'  → shown as an active card with a CTA link
 *   - status 'closed'           → kept for the record, shown muted under
 *                                 '지난 공고 / Past Postings' with no CTA
 *   - no active positions       → empty-state message (closed ones still listed)
 *
 * A posting with a `deadline` also closes itself the day after it — see
 * `getEffectiveStatus()`. A `deadline: null` posting is open until filled
 * (채용 시까지) and only ever closes by an explicit `status: 'closed'`,
 * which stays the permanent record either way.
 */

import type { BilingualText } from '@/utils/i18n'

export type PositionTrack = 'T1' | 'T2' | 'T3' | 'T4' | 'T5' | 'T6' | 'T8' | 'T9'
export type PositionLevel = 'senior' | 'junior'
export type PositionStatus = 'open' | 'closed' | 'urgent'

export interface Position {
  /** Lowercase URL segment — see the note above. */
  id: string
  /**
   * Internal recruiting-plan track. Kept for cross-referencing the JD notes;
   * deliberately **not** rendered — 'T2'/'T3' mean nothing to a candidate.
   */
  track: PositionTrack
  level: PositionLevel
  status: PositionStatus
  title: BilingualText
  description: BilingualText
  /** Work location, shown on the listing card and the detail-page header. */
  location: BilingualText
  /**
   * ISO date string, e.g. '2026-08-17' — the last day applications are
   * accepted (inclusive); the posting closes on its own the next day.
   * `null` = open until filled (채용 시까지): never auto-closes, rendered as
   * '채용 시까지 / Open until filled' wherever a date would appear.
   */
  deadline: string | null
}

/** All current postings work at the Daejeon HQ + corporate R&D institute. */
const DAEJEON: BilingualText = {
  en: 'Daejeon, South Korea',
  ko: '대전, 대한민국',
}

export const positions: Position[] = [
  // October 2026 intake — latest web-posting HTMLs (2026-10-01).
  {
    id: 'thermal-systems-design-analysis-engineer',
    track: 'T1',
    level: 'senior',
    status: 'open',
    title: {
      en: 'Senior Thermal Systems Design & Analysis Engineer',
      ko: '열시스템 설계·해석 책임 엔지니어'
    },
    description: {
      en: 'Own IsoTES® thermal and mass balances, cycle models, equipment design points and operating ranges. 5+ years; annual salary from KRW 70 million.',
      ko: 'IsoTES® 열·물질수지와 사이클 모델링, 장비 설계점·운전범위를 책임집니다. 경력 5년 이상 · 연봉 7,000만원 이상.'
    },
    deadline: null,
    location: DAEJEON
  },
  {
    id: 'heat-exchanger-thermal-design-engineer',
    track: 'T4',
    level: 'senior',
    status: 'open',
    title: {
      en: 'Senior Heat Exchanger Thermal Design Engineer',
      ko: '열교환기 열설계 책임 엔지니어'
    },
    description: {
      en: 'Own heat exchanger rating, sizing, performance guarantees and vendor verification. 7+ years; annual salary from KRW 80 million.',
      ko: '열교환기 rating·sizing, 열성능 보증과 제작사 성능검증을 책임집니다. 경력 7년 이상 · 연봉 8,000만원 이상.'
    },
    deadline: null,
    location: DAEJEON
  },
  {
    id: 'plant-mechanical-design-engineer',
    track: 'T2',
    level: 'senior',
    status: 'open',
    title: {
      en: 'Senior Plant Mechanical Design Engineer',
      ko: '플랜트 기계설계 책임 엔지니어'
    },
    description: {
      en: 'Turn process and thermal requirements into buildable plant layouts, piping and equipment designs. 5+ years; annual salary from KRW 70 million.',
      ko: '공정·열설계 요구사항을 설비 배치·배관·장치 설계와 제작·현장 변경으로 구현합니다. 경력 5년 이상 · 연봉 7,000만원 이상.'
    },
    deadline: null,
    location: DAEJEON
  },
  {
    id: 'electrical-control-engineer-2026',
    track: 'T3',
    level: 'senior',
    status: 'open',
    title: {
      en: 'Electrical & Control Engineer',
      ko: '전기·제어 엔지니어'
    },
    description: {
      en: 'Implement panel ratings, electrical drawings, PLC and HMI from the CTO’s control concept. 3+ years; annual salary from KRW 60 million.',
      ko: 'CTO의 제어 개념을 바탕으로 판넬 정격·전기 도면·PLC·HMI를 구현합니다. 경력 3년 이상 · 연봉 6,000만원 이상.'
    },
    deadline: null,
    location: DAEJEON
  },
  // Previous postings are preserved at their published URLs.

  {
    id: 't1-senior',
    track: 'T1',
    level: 'senior',
    status: 'closed',
    title: {
      en: 'Senior Thermal Systems Engineer',
      ko: '열시스템 책임 엔지니어',
    },
    description: {
      en: 'Lead decision-maker for HTS/IU-IsoTES® media and cycle design. Drive the core IsoTES® IP alongside the CTO.',
      ko: 'HTS/IU-IsoTES® 매체·사이클 설계의 의사결정 책임자. CTO와 함께 IsoTES® IP 핵심을 끌고 갑니다.',
    },
    location: DAEJEON,
    deadline: '2026-07-16',
  },
  {
    id: 'pressure-vessel-tank-design-engineer',
    track: 'T2',
    level: 'senior',
    status: 'closed',
    title: {
      en: 'Pressure Vessel & Tank Design Engineer',
      ko: '압력용기·탱크 설계 엔지니어',
    },
    description: {
      en: 'Take the CTO\'s P&ID and design the structural strength of our pressure vessels and tanks — the thickness you calculate goes up on site as an IsoTES® plant.',
      ko: 'CTO의 P&ID를 받아 압력용기·탱크의 구조 강도를 직접 설계합니다. 계산한 두께와 재질이 IsoTES® 실증 설비로 현장에 그대로 세워집니다.',
    },
    location: DAEJEON,
    deadline: null,
  },
  {
    id: 'thermal-energy-storage-modeler',
    track: 'T2',
    level: 'junior',
    status: 'closed',
    title: {
      en: 'Thermal Energy Storage System Modeler',
      ko: '열에너지 저장시스템 모델러',
    },
    description: {
      en: 'Turn the design engineer\'s numbers into 3D models and detailed drawings, and manage fabrication vendors on schedule and quality. Open to any major, any level of experience.',
      ko: '설계 엔지니어가 정한 값을 3D 모델과 상세 도면으로 구현하고, 외주 제작 일정·품질을 직접 관리합니다. 경력·전공 무관.',
    },
    location: DAEJEON,
    deadline: null,
  },
  {
    id: 'electrical-control-engineer',
    track: 'T3',
    level: 'senior',
    status: 'closed',
    title: {
      en: 'Electrical & Control Engineer',
      ko: '전기·제어 엔지니어',
    },
    description: {
      en: 'Own the full electrical and control scope — panel ratings, drawings, PLC implementation — starting from the control concept set by the CTO.',
      ko: 'CTO가 잡은 제어 개념을 받아 판넬 정격 산정부터 도면·PLC 구현까지 전기·제어 전 과정을 직접 맡습니다.',
    },
    location: DAEJEON,
    deadline: null,
  },
  // Add more positions here as recruiting progresses.
  // Dated postings close on their own the day after `deadline`; a
  // deadline: null posting stays open until filled. Set status: 'closed'
  // (do not delete) when one is filled so the record is preserved — either
  // way it moves to the 'Past Postings' section.
]

const ACTIVE_STATUSES: PositionStatus[] = ['urgent', 'open']

/** Today's date in Korea (UTC+9) as YYYY-MM-DD — deadlines are Korean dates. */
function todayInKorea(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/**
 * The status to render, with the deadline applied: a posting whose deadline
 * has passed counts as closed without anyone editing this file. Compared as
 * YYYY-MM-DD strings, so '2026-08-17' stays open through all of 17 Aug and is
 * closed from 18 Aug. A `deadline: null` posting (open until filled) never
 * closes here — only an explicit status: 'closed' does.
 *
 * Note the prerendered snapshot freezes this at build time. The SPA recomputes
 * on load, so visitors always see the right state, but a rebuild is what
 * refreshes the static HTML that crawlers get.
 */
export function getEffectiveStatus(position: Position): PositionStatus {
  if (position.status === 'closed') return 'closed'
  if (position.deadline === null) return position.status
  return todayInKorea() > position.deadline ? 'closed' : position.status
}

/** Active postings (open/urgent), shown as the main grid. */
export function getVisiblePositions(): Position[] {
  return positions.filter((p) => ACTIVE_STATUSES.includes(getEffectiveStatus(p)))
}

/** Closed postings, shown muted under 'Past Postings'. */
export function getClosedPositions(): Position[] {
  return positions.filter((p) => getEffectiveStatus(p) === 'closed')
}

/** Look up a single position by id (used by the detail page / routing). */
export function getPositionById(id: string): Position | undefined {
  return positions.find((p) => p.id === id)
}
